import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, api } from '../../lib/api';
import type { LoginResponse } from '../../lib/types';
import { useSessionChanged } from '../../lib/session';
import { credentialToJson, isPasskeySupported, parseRequestOptions } from '../../lib/webauthn';
import { Alert, ErrorMessage, Field, PageTitle } from '../../components/ui';
import { GoogleButton } from '../../components/GoogleButton';

const ERRORS: Record<string, string> = {
  google: 'Nie udało się zalogować przez Google. Spróbuj ponownie.',
  'konto-istnieje':
    'Konto z tym adresem e-mail już istnieje. Zaloguj się dotychczasową metodą, a następnie połącz konto Google w ustawieniach konta.',
  'brak-zaproszenia': 'Nie znaleziono konta ani zaproszenia dla tego konta Google. Poproś placówkę o zaproszenie.',
  'email-niezweryfikowany': 'Adres e-mail w koncie Google nie jest zweryfikowany.',
  'konto-nieaktywne': 'To konto jest nieaktywne. Skontaktuj się z placówką.',
  'konto-zablokowane': 'Konto jest tymczasowo zablokowane po zbyt wielu próbach logowania.',
  'wymagany-klucz-dostepu': 'Twoje konto jest chronione kluczem dostępu – zaloguj się przyciskiem „Zaloguj kluczem dostępu”.',
  'sesja-wygasla': 'Sesja wygasła. Zaloguj się ponownie.',
};

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionChanged = useSessionChanged();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'password' | 'twoFactor' | 'passkey'>('password');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const returnUrl = safeReturnUrl(params.get('returnUrl'));
  const redirectError = params.get('blad');

  async function finish() {
    await sessionChanged();
    navigate(returnUrl ?? '/', { replace: true });
  }

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<LoginResponse>('/api/v1/auth/login', { email, password });
      if (result.status === 'twoFactorRequired') setStep('twoFactor');
      else if (result.status === 'passkeyRequired') setStep('passkey');
      else await finish();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function signInWithPasskey() {
    setBusy(true);
    setError(null);
    try {
      const options = await api.post<Record<string, unknown>>('/api/v1/auth/login/passkey/options');
      const credential = (await navigator.credentials.get({ publicKey: parseRequestOptions(options) })) as PublicKeyCredential | null;
      if (!credential) return;
      await api.post('/api/v1/auth/login/passkey', { credential: credentialToJson(credential) });
      await finish();
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(400, { title: 'Nie udało się użyć klucza dostępu' }));
    } finally {
      setBusy(false);
    }
  }

  if (step === 'twoFactor') {
    return <TwoFactorForm onDone={finish} />;
  }

  return (
    <section className="auth-card" aria-labelledby="login-title">
      <PageTitle>
        <span id="login-title">Logowanie</span>
      </PageTitle>
      {redirectError && <Alert>{ERRORS[redirectError] ?? 'Nie udało się zalogować.'}</Alert>}
      {step === 'passkey' && <Alert kind="info">Dokończ logowanie kluczem dostępu.</Alert>}
      <ErrorMessage error={error} />

      <div className="auth-options">
        <GoogleButton href={`/api/v1/auth/external/google?mode=login${returnUrl ? `&returnUrl=${encodeURIComponent(returnUrl)}` : ''}`} />
        {isPasskeySupported() && (
          <button type="button" className="secondary" onClick={() => void signInWithPasskey()} disabled={busy}>
            Zaloguj kluczem dostępu
          </button>
        )}
      </div>

      <p className="divider">
        <span>lub e-mailem i hasłem</span>
      </p>

      <form onSubmit={(e) => void submitPassword(e)} noValidate>
        <Field label="E-mail" name="email" type="email" autoComplete="username webauthn" value={email} onChange={setEmail} required />
        <Field label="Hasło" name="password" type="password" autoComplete="current-password" value={password} onChange={setPassword} required />
        <button type="submit" disabled={busy}>
          Zaloguj
        </button>
      </form>
      <p>
        <Link to="/reset-hasla">Nie pamiętasz hasła?</Link>
      </p>
    </section>
  );
}

export function TwoFactorForm({ onDone }: { onDone: () => Promise<void> }) {
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/auth/login/two-factor', useRecovery ? { recoveryCode: code } : { code });
      await onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="auth-card">
      <PageTitle>Weryfikacja dwuetapowa</PageTitle>
      <ErrorMessage error={error} />
      <form onSubmit={(e) => void submit(e)}>
        <Field
          label={useRecovery ? 'Kod odzyskiwania' : 'Kod z aplikacji uwierzytelniającej'}
          name="code"
          value={code}
          onChange={setCode}
          inputMode={useRecovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          required
        />
        <button type="submit" disabled={busy}>
          Potwierdź
        </button>
      </form>
      <button type="button" className="link-button" onClick={() => setUseRecovery(!useRecovery)}>
        {useRecovery ? 'Użyj kodu z aplikacji' : 'Nie mam dostępu do aplikacji – użyj kodu odzyskiwania'}
      </button>
    </section>
  );
}

/** Second step after signing in with Google (/logowanie/2fa). */
export function TwoFactorPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionChanged = useSessionChanged();
  return (
    <TwoFactorForm
      onDone={async () => {
        await sessionChanged();
        navigate(safeReturnUrl(params.get('returnUrl')) ?? '/', { replace: true });
      }}
    />
  );
}

function safeReturnUrl(value: string | null): string | null {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : null;
}
